import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { GISMap } from "@/components/GISMap";
import {
  Landmark, Pickaxe, FileText, ShieldCheck, Banknote, Gavel, ClipboardCheck, TrendingUp,
  Sparkles, Loader2,
} from "lucide-react";

const COLORS = ["#C99A2E", "#145A7A", "#2F6B4F", "#A96A35", "#7CBFDC", "#6BB88A", "#E67E7E", "#B58BC2"];

const Kpi = ({ icon: Icon, label, value, sub, tone = "gold", testid }) => (
  <div className="cad-panel p-5 relative overflow-hidden" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">{label}</div>
        <div className="cad-kpi-value text-3xl mt-2 text-[#F6F8FA]">{value}</div>
        {sub && <div className="text-[11px] text-[#B3C2CC] mt-1">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-sm cad-glass flex items-center justify-center ${tone === "gold" ? "text-[#C99A2E]" : tone === "blue" ? "text-[#7CBFDC]" : "text-[#6BB88A]"}`}>
        <Icon size={18} strokeWidth={1.6} />
      </div>
    </div>
  </div>
);

export const Dashboard = () => {
  const { user } = useAuth();
  const overviewQ = useQuery({
    queryKey: ["executive-overview"],
    queryFn: async () => (await api.get("/executive/overview")).data,
  });
  const briefQ = useQuery({
    queryKey: ["executive-brief"],
    queryFn: async () => (await api.post("/ai/brief")).data,
    enabled: false,
  });

  const parcelsQ = useQuery({
    queryKey: ["parcels-map"],
    queryFn: async () => (await api.get("/land/parcels", { params: { limit: 80 } })).data.items,
  });
  const mineralsQ = useQuery({
    queryKey: ["minerals-map"],
    queryFn: async () => (await api.get("/mining/rights", { params: { limit: 120 } })).data.items,
  });

  const data = overviewQ.data;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">National Situation Room</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#F6F8FA] mt-1">
            Good day, {user?.name?.split(" ")[0] || "Commissioner"}.
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">
            Sovereign snapshot of Zimbabwe's land, survey and mineral-rights posture. All figures based on demonstration data.
          </div>
        </div>
        <button
          onClick={() => briefQ.refetch()}
          data-testid="btn-generate-brief"
          className="cad-btn-primary px-4 py-2.5 rounded-sm inline-flex items-center gap-2 text-sm"
        >
          {briefQ.isFetching ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Generate Executive Brief
        </button>
      </div>

      {briefQ.data && (
        <div className="cad-panel p-5" data-testid="brief-panel">
          <div className="flex items-center gap-2 text-[11px] font-mono-cad uppercase tracking-[0.22em] text-[#C99A2E]">
            <Sparkles size={14} /> Sovereign Intelligence Brief
          </div>
          <div className="text-[14px] text-[#F6F8FA] mt-3 leading-relaxed">{briefQ.data.brief}</div>
        </div>
      )}

      {overviewQ.isLoading && (
        <div className="cad-panel p-10 text-center font-mono-cad text-xs uppercase tracking-[0.22em] text-[#B3C2CC]">
          Compiling national snapshot…
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="kpi-parcels" icon={Landmark} label="Parcels Registered" value={data.kpis.parcels_total.toLocaleString()}
                 sub={`${data.kpis.parcels_titled.toLocaleString()} titled`} tone="gold" />
            <Kpi testid="kpi-mining" icon={Pickaxe} label="Mineral Rights" value={data.kpis.mining_total.toLocaleString()}
                 sub={`${data.kpis.mining_active} active · ${data.kpis.mining_pending} pending`} tone="blue" />
            <Kpi testid="kpi-apps" icon={FileText} label="Applications" value={data.kpis.applications_total.toLocaleString()}
                 sub={`${data.kpis.applications_pending} in review`} tone="green" />
            <Kpi testid="kpi-revenue" icon={Banknote} label="Revenue (USD)" value={`$${Math.round(data.kpis.revenue_collected).toLocaleString()}`}
                 sub={`$${Math.round(data.kpis.revenue_outstanding).toLocaleString()} outstanding`} tone="gold" />
            <Kpi testid="kpi-compliance" icon={ShieldCheck} label="Compliance Watch" value={data.kpis.mining_expiring}
                 sub="Expiring within 120 days" tone="gold" />
            <Kpi testid="kpi-disputes" icon={Gavel} label="Open Disputes" value={data.kpis.disputes_open} tone="blue" />
            <Kpi testid="kpi-inspections" icon={ClipboardCheck} label="Inspections Due" value={data.kpis.inspections_due} tone="green" />
            <Kpi testid="kpi-conflicts" icon={TrendingUp} label="Conflict Reviews"
                 value={data.applications_by_status.find((s) => s.status === "conflict_review")?.count || 0} tone="gold" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="cad-panel p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Provincial Ledger</div>
                  <div className="font-display text-lg font-bold text-[#F6F8FA]">Parcels registered by province</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.provinces} layout="vertical" margin={{ left: 24, right: 12 }}>
                  <CartesianGrid stroke="#1e3245" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#7C93A5" fontSize={11} />
                  <YAxis dataKey="province" type="category" stroke="#DCE3E8" fontSize={11} width={140} />
                  <Tooltip contentStyle={{ background: "#0B3558", border: "1px solid #C99A2E", color: "#F6F8FA" }} />
                  <Bar dataKey="parcels" fill="#C99A2E" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Commodities Pipeline</div>
              <div className="font-display text-lg font-bold text-[#F6F8FA]">Mineral rights by commodity</div>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={data.commodities} dataKey="count" nameKey="commodity" innerRadius={55} outerRadius={100} paddingAngle={2}>
                    {data.commodities.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0B3558", border: "1px solid #C99A2E", color: "#F6F8FA" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {data.commodities.slice(0, 8).map((c, i) => (
                  <div key={c.commodity} className="flex items-center gap-2 text-[11px] text-[#DCE3E8]">
                    <div className="w-2.5 h-2.5" style={{ background: COLORS[i % COLORS.length] }}></div>
                    <span className="truncate">{c.commodity}</span>
                    <span className="ml-auto font-mono-cad">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="cad-panel p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">National GIS</div>
                  <div className="font-display text-lg font-bold text-[#F6F8FA]">Land parcels & mineral polygons</div>
                </div>
                <div className="flex gap-2">
                  <span className="cad-chip cad-chip-green">Parcels</span>
                  <span className="cad-chip cad-chip-gold">Active</span>
                  <span className="cad-chip cad-chip-blue">Pending</span>
                  <span className="cad-chip cad-chip-red">Conflict</span>
                </div>
              </div>
              <GISMap parcels={parcelsQ.data || []} minerals={mineralsQ.data || []} height={420} />
            </div>

            <div className="cad-panel p-5">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5]">Workflow Snapshot</div>
              <div className="font-display text-lg font-bold text-[#F6F8FA] mb-3">Applications by status</div>
              <div className="space-y-2.5">
                {data.applications_by_status.map((s, i) => {
                  const max = Math.max(...data.applications_by_status.map((x) => x.count));
                  return (
                    <div key={s.status}>
                      <div className="flex items-center justify-between text-[12px] mb-1">
                        <span className="text-[#DCE3E8] capitalize">{(s.status || "unknown").replace(/_/g, " ")}</span>
                        <span className="font-mono-cad text-[#F6F8FA]">{s.count}</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                        <div className="h-full" style={{
                          width: `${(s.count / max) * 100}%`,
                          background: COLORS[i % COLORS.length],
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
